import SectionMessage from '@atlaskit/section-message';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import {
  getCurrentMACContactDetails,
  getCurrentMACContactEmail,
  selectCMTSelfServeLink,
  selectIsBtfAlreadyUsedForCMT,
  selectManageAppLinkForBtfUnderCmt,
  selectSiteNameWithDomainForBtfUnderCmt,
} from '../../duck/selectors';
import { RESTRICTED_COUNTRIES_ISO_CODES, RESTRICTED_EMAIL_DOMAINS } from '../../../utils';
import { extractTLDFrom } from '../../../siteconfigurator/utils';
import config from 'appconfig';
import { State } from 'model/State';
import { deleteFromHamsClient } from 'util/mac/api/cloud/delete';
import { fetchCMTSelfServeLink } from '../../duck/actions/accountId';
import {
  fetchIsBtfAlreadyUsedForCMT,
  fetchManageAppLinkForBtfUnderCmt,
  fetchSiteNameWithDomainForBtfUnderCmt,
} from '../../duck/actions/btfAccountId';

const MERCHANDISE_STASH_PRODUCT_KEYS = [
  'stash',
  'pricingplan.stash',
  'pricingplan.stash-data-center',
];

const NoCostCloudMigrationBanner = ({ account }) => {
  const isStashIncluded = MERCHANDISE_STASH_PRODUCT_KEYS.includes(
    account?.merchandise.productPackage.key,
  );
  return (
    <SectionMessage appearance="discovery">
      <span>
        <strong>
          <FormattedMessage
            id="mac.licenses.cloud.migration.banner.title"
            defaultMessage="Cloud migration trial offer"
          />
        </strong>
        <br />
        {isStashIncluded ? (
          <FormattedMessage
            id="mac.licenses.cloud.migration.banner.no-cost.description.with-stash"
            defaultMessage="Atlassian would like to offer you a no-cost cloud migration trial for the {cloudProductDescription}."
            values={{
              cloudProductDescription: account?.migrationEvaluationDTO?.cloudProductDescription,
            }}
          />
        ) : (
          <FormattedMessage
            id="mac.licenses.cloud.migration.banner.no-cost.description"
            defaultMessage="Atlassian would like to offer you a no-cost cloud migration trial for the {cloudProductDescription} Standard plan. This trial is good for up to {usageLimit} {unitLabel}"
            values={{
              usageLimit: account?.migrationEvaluationDTO?.usageLimit,
              unitLabel: account?.migrationEvaluationDTO?.unitLabel,
              cloudProductDescription: account?.migrationEvaluationDTO?.cloudProductDescription,
            }}
          />
        )}{' '}
        <a href="https://www.atlassian.com/cloud-migration/explore-cloud">
          <FormattedMessage id="mac.licenses.cloud.migration.learn" defaultMessage="Learn more" />
        </a>
        <br />
        <a
          href={`https://atlassian.com/try/cloud/signup?bundle=${account?.migrationEvaluationDTO?.bundleKey}&migrationSourceUuid=${account?.migrationEvaluationDTO?.migrationSourceUuid}&source=mac`}
        >
          <FormattedMessage
            id="mac.licenses.cloud.migration.start"
            defaultMessage="Start my trial"
          />
        </a>
        <br />
        <i>
          <FormattedMessage
            id="mac.licenses.cloud.migration.trial.only"
            defaultMessage="The trial is only for use by this company."
          />
        </i>
      </span>
    </SectionMessage>
  );
};

const CCPCmtExtendedTrial = ({ account }) => (
  <SectionMessage appearance="discovery">
    <span>
      <strong>
        <FormattedMessage
          id="mac.licenses.cloud.migration.banner.title.extended-trial-active-at"
          defaultMessage="Your {cloudProductDescription} extended trial is active at"
          values={{
            cloudProductDescription: account?.migrationEvaluationDTO?.cloudProductDescription,
          }}
        />
      </strong>{' '}
      <a href={account?.migrationEvaluationDTO?.cloudSiteUrl}>
        {account?.migrationEvaluationDTO?.cloudSiteDisplayName}
      </a>
      <br />
      <a href="https://www.atlassian.com/company/contact/purchasing-licensing#/">
        <FormattedMessage
          id="mac.licenses.cloud.migration.cancel"
          defaultMessage="Cancel my cloud migration trial"
        />
      </a>
    </span>
  </SectionMessage>
);

const ProvisionedBanner = ({ account }) => {
  const state = useSelector<State, State>((s) => s);
  const [error, setError] = useState<string | null>(null);
  const isStashIncluded = MERCHANDISE_STASH_PRODUCT_KEYS.includes(
    account?.merchandise.productPackage.key,
  );

  const handleCancelCloudMigrationTrial = (productKey) => {
    deleteFromHamsClient(
      `/${account?.migrationEvaluationDTO?.cloudSiteUUID}/extendedTrial/${productKey}/clear`,
      state,
    )
      .then(() => {
        window.location.reload();
        setError(null);
      })
      .catch((e) => {
        setError(e);
      });
  };

  return isStashIncluded ? (
    <SectionMessage appearance="discovery">
      <span>
        <strong>
          <FormattedMessage
            id="mac.licenses.cloud.migration.banner.title.extended-trial-active-at-workspace"
            defaultMessage="Your {cloudProductDescription} extended trial is active at workspace"
            values={{
              cloudProductDescription: account?.migrationEvaluationDTO?.cloudProductDescription,
            }}
          />
        </strong>
        <br />
        <a
          href={`${config.bitbucketCmtUrl}${account?.migrationEvaluationDTO.workspace}/workspace/overview`}
        >
          {account?.migrationEvaluationDTO.workspace}
        </a>
        <br />
        <FormattedMessage
          id="mac.licenses.cloud.migration.banner.provisioned.start-end.trial.with-stash"
          defaultMessage="Your trial started on {startDate}. It runs until {endDate}"
          values={{
            startDate: account?.migrationEvaluationDTO?.startDate,
            endDate: account?.migrationEvaluationDTO?.endDate,
          }}
        />
        <br />
        <a href="#" onClick={() => handleCancelCloudMigrationTrial('bitbucket')}>
          <FormattedMessage
            id="mac.licenses.cloud.migration.cancel"
            defaultMessage="Cancel my cloud migration trial"
          />
        </a>
      </span>
      {error ? (
        <FormattedMessage
          id="mac.licenses.cloud.migration.banner.provisioned.error.bitbucket"
          defaultMessage="Error removing extended cloud trial for Bitbucket cloudSiteUUID={cloudSiteUUID} error={error}"
          values={{
            cloudSiteUUID: account?.migrationEvaluationDTO?.cloudSiteUUID,
            error,
          }}
        />
      ) : null}
    </SectionMessage>
  ) : (
    <SectionMessage appearance="discovery">
      <span>
        <strong>
          <FormattedMessage
            id="mac.licenses.cloud.migration.banner.title.extended-trial-active-at"
            defaultMessage="Your {cloudProductDescription} extended trial is active at"
            values={{
              cloudProductDescription: account?.migrationEvaluationDTO?.cloudProductDescription,
            }}
          />
        </strong>{' '}
        <a href={account?.migrationEvaluationDTO?.cloudSiteUrl}>
          {account?.migrationEvaluationDTO?.cloudSiteDisplayName}
        </a>
        <br />
        <FormattedMessage
          id="mac.licenses.cloud.migration.banner.provisioned.start-end.trial"
          defaultMessage="Your trial started on {startDate}. It runs until {endDate} and includes up to {usageLimit} {unitLabel}."
          values={{
            startDate: account?.migrationEvaluationDTO?.startDate,
            endDate: account?.migrationEvaluationDTO?.endDate,
            usageLimit: account?.migrationEvaluationDTO?.usageLimit,
            unitLabel: account?.migrationEvaluationDTO?.unitLabel,
          }}
        />
        <br />
        <a
          href="#"
          onClick={() =>
            handleCancelCloudMigrationTrial(account?.migrationEvaluationDTO?.cloudChildAccountId)
          }
        >
          <FormattedMessage
            id="mac.licenses.cloud.migration.cancel"
            defaultMessage="Cancel my cloud migration trial"
          />
        </a>
      </span>
      {error ? (
        <FormattedMessage
          id="mac.licenses.cloud.migration.banner.provisioned.error"
          defaultMessage="Error removing extended cloud trial cloudSiteUUID={cloudSiteUUID} childCloudAccountId={cloudChildAccountId} error={error}"
          values={{
            cloudSiteUUID: account.migrationEvaluationDTO.cloudSiteUUID,
            cloudChildAccountId: account.migrationEvaluationDTO.cloudChildAccountId,
            error,
          }}
        />
      ) : null}
    </SectionMessage>
  );
};

const CmtSelfServeMigrationBanner = ({ account, cmtSelfServeLink }) => {
  const isBtfAlreadyUsedForCMT = useSelector(selectIsBtfAlreadyUsedForCMT);
  const manageAppLinkForBtfUnderCmt = useSelector(selectManageAppLinkForBtfUnderCmt);
  const siteNameWithDomainForBtfUnderCmt = useSelector(selectSiteNameWithDomainForBtfUnderCmt);

  const showBtfUnderCmtBanner =
    isBtfAlreadyUsedForCMT &&
    siteNameWithDomainForBtfUnderCmt != null &&
    manageAppLinkForBtfUnderCmt != null;

  return showBtfUnderCmtBanner ? (
    <SectionMessage appearance="discovery">
      <span>
        <strong>
          <FormattedMessage
            id="mac.licenses.cloud.migration.banner.title.extended-trial-active-at"
            defaultMessage="Your {cloudProductDescription} extended trial is active at"
            values={{
              cloudProductDescription: account?.merchandise?.productPackage?.description,
            }}
          />
        </strong>{' '}
        <a href={siteNameWithDomainForBtfUnderCmt as string}>{siteNameWithDomainForBtfUnderCmt}</a>
        <br />
        <FormattedMessage
          id="mac.licenses.cloud.migration.banner.more-details-visit"
          defaultMessage="For more details, visit "
        />
        <a href={manageAppLinkForBtfUnderCmt as string}>{manageAppLinkForBtfUnderCmt}</a>
      </span>
    </SectionMessage>
  ) : (
    <SectionMessage appearance="discovery">
      <span>
        <strong>
          <FormattedMessage
            id="mac.licenses.cloud.migration.banner.title"
            defaultMessage="Cloud migration trial offer"
          />
        </strong>
        <br />
        <FormattedMessage
          id="mac.licenses.cloud.migration.banner.description.free-cloud-trial"
          defaultMessage="You can now avail free cloud migration trial for {description}. The trial period will be extended to remainder of server maintenance or Data Center license of this app."
          values={{
            description: account.merchandise.productPackage.description,
          }}
        />
        <br />
        <a href={cmtSelfServeLink}>
          <FormattedMessage
            id="mac.licenses.cloud.migration.default.activate"
            defaultMessage="Activate trial"
          />
        </a>
      </span>
    </SectionMessage>
  );
};

export const CloudMigrationTrialOfferBanner = ({ account }) => {
  const [isRUBYRestricted, setIsRUBYRestricted] = useState(true);
  const currentMACContactEmail = useSelector(getCurrentMACContactEmail);
  const currentMACContactDetails = useSelector(getCurrentMACContactDetails);
  const cmtSelfServeLink = useSelector(selectCMTSelfServeLink);

  useEffect(() => {
    if (currentMACContactDetails && currentMACContactEmail) {
      const isISORestricted = RESTRICTED_COUNTRIES_ISO_CODES.includes(
        (currentMACContactDetails as any)?.organisationDetails?.isoCountryCode,
      );
      const isEmailRestricted = RESTRICTED_EMAIL_DOMAINS.includes(
        extractTLDFrom(currentMACContactEmail),
      );
      if (!isISORestricted && !isEmailRestricted) {
        setIsRUBYRestricted(false);
      }
    }
  }, [currentMACContactDetails, currentMACContactEmail]);

  useEffect(() => {
    fetchIsBtfAlreadyUsedForCMT();
    fetchManageAppLinkForBtfUnderCmt();
    fetchSiteNameWithDomainForBtfUnderCmt();
    fetchCMTSelfServeLink();
  }, []);

  const showNoCostCloudMigrationBanner =
    account?.migrationEvaluationDTO?.migrationSourceUuid != null &&
    account?.migrationEvaluationDTO?.eligibleForExtendedCloudTrial &&
    !isRUBYRestricted &&
    !account?.migrationEvaluationDTO?.accountOnCcpCmt;

  const showCCPCmtBanner = account.migrationEvaluationDTO?.accountOnCcpCmt;
  const showProvisionedBanner = account.migrationEvaluationDTO?.provisioned;

  if (showNoCostCloudMigrationBanner) {
    return <NoCostCloudMigrationBanner account={account} />;
  }

  if (showCCPCmtBanner) {
    return <CCPCmtExtendedTrial account={account} />;
  }

  if (showProvisionedBanner) {
    return <ProvisionedBanner account={account} />;
  }

  if (cmtSelfServeLink) {
    return <CmtSelfServeMigrationBanner account={account} cmtSelfServeLink={cmtSelfServeLink} />;
  }

  return null;
};
